/**
 * Classements joueurs du tournoi (buteurs / passeurs / notes), servis par
 * /data/leaderboards.json — agrégés CÔTÉ SERVEUR depuis l'archive Supabase
 * (server/leaderboards.mjs), donc coût API nul. Indisponible (503) hors prod
 * Supabase : la section se masque, jamais d'écran cassé (règle 8).
 */
export interface LeaderRow {
  readonly id: number;
  readonly name: string | null;
  readonly photo: string | null;
  readonly teamCode: string | null;
  readonly position: string | null;
  readonly goals: number;
  readonly assists: number;
  readonly apps: number;
  readonly minutes: number;
  readonly rating: number | null;
}

/** Équipe type du tournoi (4-3-3) : meilleurs notés par poste. */
export interface TournamentXI {
  readonly formation: string;
  readonly gk: readonly LeaderRow[];
  readonly def: readonly LeaderRow[];
  readonly mid: readonly LeaderRow[];
  readonly fwd: readonly LeaderRow[];
}

/** Détail d'un joueur d'un club au Mondial (note-tournoi + buts/passes/matchs). */
export interface ClubContributor {
  readonly id: number;
  readonly name: string | null;
  readonly teamCode: string | null;
  readonly rating: number;
  readonly goals: number;
  readonly assists: number;
  readonly apps: number;
}
export interface ClubRating {
  readonly club: string;
  readonly clubLogo: string | null;
  readonly rating: number;
  readonly players: number; // nombre de joueurs notés
  readonly matches: number;
  readonly goals: number;
  readonly assists: number;
  readonly roster: readonly ClubContributor[]; // détail par joueur (clic → modale)
}

/** Moyenne de compétition d'un poste pour chaque métrique dérivée (clés = computePlayerMetrics). */
export interface PositionBenchmark {
  readonly players: number;
  readonly conversion: number | null;
  readonly gaPer90: number | null;
  readonly keyPassesPer90: number | null;
  readonly passAcc: number | null;
  readonly duelWinPct: number | null;
  readonly dribblePct: number | null;
  readonly tacklesPer90: number | null;
}

/** Ligne d'agrégat tournoi d'un joueur (table de la page Statistiques). */
export interface PlayerStat {
  readonly id: number;
  readonly name: string | null;
  readonly teamCode: string;
  readonly pos: string | null;
  readonly apps: number;
  readonly minutes: number;
  readonly goals: number;
  readonly assists: number;
  readonly shots: number;
  readonly shotsOn: number; // tirs cadrés (contexte : cadrés / total tirs)
  readonly keyPasses: number;
  readonly passes: number; // volume total de passes tentées (tournoi)
  readonly passAcc: number | null; // 0–100
  readonly duelWinPct: number | null; // 0–1
  readonly rating: number | null;
}

export interface Leaderboards {
  readonly generatedAt: string;
  readonly topScorers: readonly LeaderRow[];
  readonly topAssists: readonly LeaderRow[];
  readonly topRated: readonly LeaderRow[];
  readonly xi: TournamentXI | null;
  readonly clubRatings: readonly ClubRating[];
  /** Moyennes du tournoi par poste (situer un joueur), ou absent hors prod. */
  readonly benchmarks?: Readonly<Record<string, PositionBenchmark>>;
  /** Agrégat par joueur (toutes les stats du tournoi), ou absent hors prod. */
  readonly playerStats?: readonly PlayerStat[];
}

/** Charge les classements, ou null si indisponibles (503 hors prod). */
export async function fetchLeaderboards(): Promise<Leaderboards | null> {
  try {
    const r = await fetch("data/leaderboards.json");
    if (!r.ok) return null;
    const doc = (await r.json()) as Partial<Leaderboards> | null;
    if (!doc || !Array.isArray(doc.topScorers)) return null;
    const xiDoc = doc.xi;
    const xi: TournamentXI | null =
      xiDoc && Array.isArray(xiDoc.gk)
        ? {
            formation: typeof xiDoc.formation === "string" ? xiDoc.formation : "4-3-3",
            gk: xiDoc.gk ?? [],
            def: xiDoc.def ?? [],
            mid: xiDoc.mid ?? [],
            fwd: xiDoc.fwd ?? [],
          }
        : null;
    return {
      generatedAt: typeof doc.generatedAt === "string" ? doc.generatedAt : "",
      topScorers: doc.topScorers ?? [],
      topAssists: doc.topAssists ?? [],
      topRated: doc.topRated ?? [],
      xi,
      clubRatings: Array.isArray(doc.clubRatings) ? doc.clubRatings : [],
      ...(doc.benchmarks && typeof doc.benchmarks === "object" ? { benchmarks: doc.benchmarks } : {}),
      ...(Array.isArray(doc.playerStats) ? { playerStats: doc.playerStats } : {}),
    };
  } catch {
    return null;
  }
}
